import { useValidation } from "Hooks/useValidation"
import { state, stateOptions } from "Models/Enums"
import React, { useState } from "react"
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button"
import { ButtonGroup } from "Views/Components/Button/ButtonGroup"
import { ComboboxOption } from "Views/Components/Combobox/Combobox"
import ComboboxSetter from "Views/Components/Combobox/ComboboxSetter"
import Modal from "Views/Components/Modal/Modal"
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter"
import { PropertySearchByRegionSection } from "./PropertySearchByRegionSection"
import { PropertySearchByTitleOptionForRegion } from "./PropertySearchByRegionType"
import ButtonAsyncState from "Views/Components/Button/ButtonAsyncState"
import alertToast from "Util/ToastifyUtils"
import axios from "axios"
import { SERVER_URL } from "Constants"
import CheckboxSetter from "Views/Components/Checkbox/CheckboxSetter"
import { LocationUtil } from "Util/LocationUtil"

type Props = {
	onClose: () => void;
}

export const RequestPropertySearchModel:React.FunctionComponent<Props> = ({ onClose }) => {
	const [fileReference, setFileReference] = useState<string>('');
	const [region, setRegion] = useState<state>('NONE');
	const [searchBy, setSearchBy] = useState<PropertySearchByTitleOptionForRegion>();
	const [searchByDetailInfo, setSearchByDetailInfo] = useState<string>();
	const [agreedToLandata, setAgreedToLandata] = useState<boolean>(false);

	const validation = useValidation({
		fileReference: {
			value: fileReference,
			isRequired: true,
			requiredText: 'File Reference is required',
		},
		region: {
			value: region,
			isRequired: true,
			requiredText: 'State is required',
			validate: v => (v.assertRule(value => value !== 'NONE', 'State is required')),
		},
		searchBy: {
			value: searchBy,
			isRequired: true,
			requiredText: 'Please select a search option',
		},
		searchByDetailInfo: {
			value: searchByDetailInfo,
			isRequired: true,
			requiredText: '',
			validate: v => v.assertTruthy('This field is required'),
		},
	});

	
	async function handlePropertySearchRequest() {
		if (validation.errorCount > 0 || !validation.isValid) {
			alertToast('Please fill all required fields before submitting a request', 'error');
		}

		try {
			const response = await axios.post<boolean>(`${SERVER_URL}/api/support-tickets?category=PropertySearch`, {
				fileReference: fileReference,
				region: region,
				propertyType: searchBy,
				propertyDetail: searchByDetailInfo,
			});

			if (response.data) {
				alertToast('Successfully submitted property search request. Our team will be in contact shortly.', 'success');
				onClose();
			} else {
				alertToast('An error occurred while processing your request, If error persist send this details via Email at processing@accessintell.com', 'error');
			}
		} catch (error) {
			if (axios.isAxiosError(error)) {
				alertToast('An error occurred while processing your request. Please try again!', 'error');
			} else {
				alertToast('An unexpected error occurred', 'error');
			}
		}
	}

	return (
		<Modal className="property-search-form" isOpen={true} label="Request Property Search Form">
			<h4>Request Property Search Form</h4>
			<TextFieldSetter
				value={fileReference}
				setValue={setFileReference}
				label="File Reference"
				placeholder="Enter matter file reference"
				isRequired
				errors={validation.get("fileReference", false)}
			/>
			<ComboboxSetter
				value={region}
				setValue={data => {setRegion(data); setSearchBy(undefined); setSearchByDetailInfo(undefined)}}
				label="Select Region"
				options={LocationUtil.getStateOptions()}
				getOptionValue={option => option}
				isRequired
				errors={validation.get("region", false)}
				searchable={false}
			/>
			{region !== 'NONE' && (
				<PropertySearchByRegionSection
					searchBy={searchBy}
					setSearchBy={setSearchBy}
					selectedRegion={region}
					searchByDetailInfo={searchByDetailInfo ?? ''}
					setSearchByDetailInfo={setSearchByDetailInfo}
					errors={validation.get("searchByDetailInfo", false)}
				/>
			)}
			{region === 'VIC' && (
				<div className="landata-agreement-section">
					<CheckboxSetter
						className="landata-agreement-checkbox"
						value={agreedToLandata}
						onChecked={(_, checked) => {setAgreedToLandata(checked)}}
						labelVisible={false}
						isRequired
					/>
					<span className="landata-agreement-consent">I agree to LANDATA agreement, incurs additional costs. Please <a className="landata-agreement-link" href={`${SERVER_URL}/api/files/document/property-search-victoria`} target="_blank" rel="noreferrer">click here</a> to view the agreement.</span>
				</div>
			)}
			<ButtonGroup className="modal__actions">
				<Button
					colors={Colors.Error}
					sizes={Sizes.Medium}
					display={Display.Text}
					onClick={onClose}
				>
					Cancel
				</Button>

				<ButtonAsyncState
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					display={Display.Solid}
					readonly={!validation.isValid || (region === 'VIC' && !agreedToLandata)}
					onPress={handlePropertySearchRequest}
				>
					Submit Request
				</ButtonAsyncState>
			</ButtonGroup>
		</Modal>
	)
}
